import { useState } from 'react';
import { Info, BookOpen, User, Calendar, Globe, Hash, FileText, Monitor } from 'lucide-react';
import type { BookMetadata, BookFormat } from '../types';
import styles from '../ReaderPanels.module.css';
import { useAlert } from '../../../contexts/AlertContext';
import { useTranslation } from '../../../i18n/useTranslation';
import { primaryButtonStyle } from './controls';

/**
 * Convert a stored Azure blob URL (or relative blob path) into the backend's
 * authenticated image proxy endpoint so the browser can actually load it.
 * Returns undefined if the URL is empty or malformed.
 */
function getProxyImageUrl(blobUrl: string | undefined): string | undefined {
  if (!blobUrl) return undefined;
  try {
    let relativePath: string;
    if (!blobUrl.startsWith('http://') && !blobUrl.startsWith('https://')) {
      relativePath = blobUrl.trim();
    } else {
      const url = new URL(blobUrl);
      const pathParts = url.pathname.split('/');
      relativePath = pathParts.slice(2).join('/');
    }
    if (!relativePath) return undefined;
    const apiBaseUrl = import.meta.env.VITE_API_URL || '';
    return `${apiBaseUrl}/api/app/books-extn/image?path=${encodeURIComponent(relativePath)}`;
  } catch (error) {
    console.error('Failed to parse cover image URL:', blobUrl, error);
    return undefined;
  }
}

interface InfoPanelProps {
  metadata: BookMetadata;
  format: BookFormat;
}

// Helper function to convert BookFormat enum to display string
function getFormatLabel(format: any): string {
  // Handle numeric enum values from backend
  if (typeof format === 'number') {
    switch (format) {
      case 1: return 'EPUB';
      case 2: return 'PDF';
      default: return 'Unknown';
    }
  }
  // Handle string values
  return String(format).toUpperCase();
}

// Helper function to get renderer type label
function getRendererTypeLabel(format: BookFormat): string {
  switch (format) {
    case 'EPUB':
      return 'EPUB.js Renderer';
    case 'PDF':
      return 'PDF.js Renderer';
    case 'CustomHtml':
      return 'Ebook Reader';
    default:
      return 'Unknown Renderer';
  }
}

/**
 * InfoPanel
 *
 * Displays book metadata and information.
 * Uses CSS modules for modern, accessible styling.
 */
export default function InfoPanel({ metadata, format }: InfoPanelProps) {
  const { t } = useTranslation();
  const { showToast } = useAlert();
  const [coverBroken, setCoverBroken] = useState(false);
  const coverProxyUrl = getProxyImageUrl(metadata.coverImageUrl);
  const showCover = !!coverProxyUrl && !coverBroken;
  const infoItems = [
    {
      icon: BookOpen,
      label: t('App.Reader.Info.Title'),
      value: metadata.title,
    },
    {
      icon: User,
      label: t('App.Reader.Info.Author'),
      value: metadata.author,
    },
    {
      icon: FileText,
      label: t('App.Reader.Info.Publisher'),
      value: metadata.publisher,
    },
    {
      icon: Hash,
      label: t('App.Reader.Info.ISBN'),
      value: metadata.isbn,
    },
    {
      icon: Calendar,
      label: t('App.Reader.Info.Published'),
      value: metadata.publishedDate,
    },
    {
      icon: FileText,
      label: t('App.Reader.Info.Format'),
      value: getFormatLabel(metadata.format),
    },
    {
      icon: Monitor,
      label: t('App.Reader.Info.Renderer'),
      value: getRendererTypeLabel(format),
    },
    {
      icon: BookOpen,
      label: t('App.Reader.Info.Pages'),
      value: metadata.pageCount?.toString(),
    },
    {
      icon: Globe,
      label: t('App.Reader.Info.Language'),
      value: metadata.language,
    },
  ].filter((item) => item.value); // Only show items with values

  return (
    <div className={styles.panelContainer} style={{ width: '100%', padding: '20px' }}>
      {/* Cover Image — only rendered when the book has a cover URL */}
      {metadata.coverImageUrl && (
        <div
          style={{
            padding: 'var(--spacing-4)',
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-neutral-50)',
          }}
        >
          {showCover ? (
            <img
              src={coverProxyUrl}
              alt={metadata.title}
              onError={() => setCoverBroken(true)}
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '192px',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-md)',
              }}
            />
          ) : (
            /* Fallback when the cover URL can't be resolved or the image fails to load */
            <div
              style={{
                width: '128px',
                height: '180px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <BookOpen
                style={{ width: '48px', height: '48px', color: 'var(--color-text-tertiary)' }}
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      )}

      {/* Information List */}
      <div className={styles.panelBody}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {infoItems.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)' }}>
                <Icon style={{ width: '20px', height: '20px', color: 'var(--color-text-tertiary)', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'var(--font-weight-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-1)' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', wordBreak: 'break-words' }}>
                    {item.value}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Description */}
          {metadata.description && (
            <div style={{ paddingTop: 'var(--spacing-4)', borderTop: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)' }}>
                <Info style={{ width: '20px', height: '20px', color: 'var(--color-text-tertiary)', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'var(--font-weight-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--spacing-1)' }}>
                    {t('App.Reader.Info.Description')}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', lineHeight: 'var(--line-height-relaxed)' }}>
                    {metadata.description}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{ padding: 'var(--spacing-4)', borderTop: '1px solid var(--color-border)', background: 'var(--color-neutral-50)' }}>
        <button
          style={{ ...primaryButtonStyle, width: '100%', justifyContent: 'center', display: 'inline-flex' }}
          onClick={() => {
            // TODO: Implement export reference
            showToast(t('App.Reader.Info.ExportComingSoon'), { type: 'info' });
          }}
          aria-label={t('App.Reader.Info.ExportReferenceAriaLabel')}
        >
          {t('App.Reader.Info.ExportReference')}
        </button>
      </div>
    </div>
  );
}
